import { api } from "@/lib/api";
import { oznaciAktivnost } from "@/lib/sesija";

export interface InboxItem {
  import_source?: string | null;
  confirmation_kind?: string | null;
  confirmation_label?: string | null;
  split_original_id?: string | null;
  unsplit_reason?: string | null;
  worker_active?: boolean | null;
  proposed_company_category?: string | null;
  suggested_company_obligation_id?: string | null;
  suggested_site_obligation_id?: string | null;
  deleted_at?: string | null;
  deleted_by_id?: string | null;
  display_title?: string | null;
  scan_owner_confirmed?: boolean;
  scan_job_id?: string | null;
  structured_backfill?: boolean;
  employee_document_id?: string;
  company_document_id?: string | null;
  company_obligation_id?: string | null;
  site_obligation_id?: string | null;
  occurrence_id?: string | null;
  id: string;
  dokument_id?: string | null;
  duplicate_of_dokument_id?: string | null;
  owner_kind: "radnik" | "firma" | "gradiliste" | "obitelj";
  family_member_id?: string | null;
  family_document_id?: string | null;
  family_relationship?: "spouse" | "child" | "other" | null;
  radnik_id: string | null;
  gradiliste_id: string | null;
  firma_kategorija: string | null;
  kategorija?: string | null;
  naziv?: string | null;
  opis?: string | null;
  owner_label?: string | null;
  status: "novo" | "predlozeno" | "potvrdeno" | "odbaceno" | "duplikat";
  ai_status: "none" | "queued" | "done" | "failed";
  ai_title: string | null;
  ai_summary: string | null;
  ai_confidence: string | number | null;
  ai_owner_mismatch: boolean;
  ai_person_full_name?: string | null;
  ai_error: string | null;
  ai_proposed_document_type_id: string | null;
  ai_proposed_issued_on: string | null;
  ai_proposed_expires_on: string | null;
  proposed_type_code?: string | null;
  proposed_type_name?: string | null;
  expiry_required?: boolean;
  original_filename?: string | null;
  mime_type?: string | null;
  uploaded_at: string;
  uploaded_by_id: string;
  uploaded_by_name?: string | null;
  source: InboxSource;
  batch_id: string;
}

export interface InboxExistingDocument {
  id: string;
  document_type_code: string;
  issued_on: string | null;
  expires_on: string | null;
  verificiran: boolean;
}
export interface StructuredCategory { kategorija: string; issued_on: string | null; expires_on: string | null }
export interface InboxStructuredFields {
  [field: string]: string | number | boolean | null | StructuredCategory[] | undefined;
}
export interface InboxStructuredBody extends InboxStructuredFields {
  document_type_code: string;
  mode: "new" | "update";
  record_id?: string | null;
}
export interface InboxStructuredCandidate { id: string; kind: string; label: string; fields: InboxStructuredFields }
export interface InboxCatalogueEntry { id: string; name: string; expiry_required?: boolean; suggested_validity_months?: number | null; expiry_review_required?: boolean }
export interface InboxDetail extends InboxItem {
  derived_fields?: string[];
  existing_documents: InboxExistingDocument[];
  structured_proposal?: InboxStructuredFields | null;
  structured_candidates?: InboxStructuredCandidate[];
  catalogues?: Record<string, InboxCatalogueEntry[]>;
  employment_type?: string | null;
  has_employment_terms?: boolean;
  missing_fields?: string[];
  naziv?: string | null;
  opis?: string | null;
}
export interface InboxPage { items: InboxItem[]; total: number }
export interface InboxBatchResult {
  confirmation_id?: string | null;
  id: string;
  outcome: "confirmed" | "rejected" | "updated" | "skipped" | "error";
  reason_code: string | null;
  message: string | null;
  employee_document_id: string | null;
  missing_fields?: string[];
}
export interface InboxBatchResponse {
  results: InboxBatchResult[];
  confirmed: number;
  rejected: number;
  skipped: number;
  errors: number;
  confidence_threshold: string | number | null;
}
export interface InboxConfirmBody {
  archive_only?: boolean;
  naziv?: string;
  opis?: string | null;
  structured?: InboxStructuredBody | null;
  document_type_code: string | null;
  attach_to_employee_document_id: string | null;
  issued_on: string | null;
  expires_on: string | null;
  radnik_id: string | null;
  verificiraj: boolean;
}

export function inboxDeletable(item: InboxItem) { return !item.deleted_at && !item.structured_backfill && ["novo", "predlozeno", "odbaceno", "duplikat"].includes(item.status); }
export const INBOX_CHANGED_EVENT = "avm:inbox-changed";
/** Confirming one of these needs zdravlje:update, whatever else a role may do in the inbox. */
export const HEALTH_TYPE_CODES: readonly string[] = ["ra2_medical_fitness", "pur_medical_certificate"];
export const HEALTH_CONFIRM_BLOCKED = "Liječničko uvjerenje potvrđuje HR (potrebna je zdravstvena ovlast).";
export function notifyInboxChanged() { window.dispatchEvent(new Event(INBOX_CHANGED_EVENT)); }
export function inboxOwnerKey(item: InboxItem) {
  return `${item.owner_kind}:${item.family_member_id ?? item.radnik_id ?? item.gradiliste_id ?? item.firma_kategorija}`;
}
export function inboxOwnerLabel(item: InboxItem) {
  if (item.import_source === "uvoz_znr_agencija" && !item.radnik_id && inboxPending(item)) return "Radnik nije prepoznat";
  return item.owner_label ?? item.firma_kategorija ?? (item.owner_kind === "obitelj" ? "Član obitelji" : item.owner_kind === "radnik" ? "Radnik" : "Gradilište");
}
export function inboxTitle(item: InboxItem) { return item.display_title || item.ai_title || item.original_filename || "Dokument"; }
export function inboxDeleted(item: InboxItem) { return !!item.deleted_at; }
export function inboxPending(item: InboxItem) { return !inboxDeleted(item) && (item.status === "novo" || item.status === "predlozeno"); }
export async function getInboxPage(params: URLSearchParams, signal?: AbortSignal): Promise<InboxPage> {
  return (await api.get<InboxPage>(`/inbox?${params.toString()}`, { signal })).data;
}
/** Fetch the complete filtered set, so group selection never silently stops at page one. */
export async function getAllInboxItems(params: URLSearchParams, signal?: AbortSignal): Promise<InboxItem[]> {
  const items = new Map<string, InboxItem>();
  for (let offset = 0; ; offset += 100) {
    const pageParams = new URLSearchParams(params);
    pageParams.set("limit", "100"); pageParams.set("offset", String(offset));
    const page = await getInboxPage(pageParams, signal);
    page.items.forEach((item) => items.set(item.id, item));
    if (offset + page.items.length >= page.total || !page.items.length) break;
  }
  return [...items.values()];
}
export async function getInboxDetail(id: string, signal?: AbortSignal, workerId?: string, typeCode?: string, backfill = false): Promise<InboxDetail> {
  const data = (await api.get<InboxDetail>(backfill ? `/inbox/structured-backfill/${id}` : `/inbox/${id}`, { signal, params: { ...(workerId ? { radnik_id: workerId } : {}), ...(typeCode ? { document_type_code: typeCode } : {}) } })).data;
  return backfill ? { ...backfillItem(data as unknown as BackfillItem), ...data } : data;
}
export async function getInboxContent(id: string, signal?: AbortSignal, backfill = false): Promise<Blob> {
  return (await api.get<Blob>(backfill ? `/inbox/structured-backfill/${id}/content` : `/inbox/${id}/content`, { responseType: "blob", signal })).data;
}
export async function getInboxAiConfig(signal?: AbortSignal): Promise<{ confidence_threshold: string | number }> {
  return (await api.get<{ confidence_threshold: string | number }>("/inbox/ai/config", { signal })).data;
}
export async function confirmInboxItem(id: string, body: InboxConfirmBody, backfill = false) {
  return (await api.post<InboxItem>(backfill ? `/inbox/structured-backfill/${id}/confirm` : `/inbox/${id}/confirm`, body)).data;
}
export async function rejectInboxItem(id: string, reject_reason: string) {
  return (await api.post<InboxItem>(`/inbox/${id}/reject`, { reject_reason })).data;
}
export async function retryInboxAi(id: string) { await api.post(`/inbox/${id}/ai/retry`); }
export function inboxAiRetryEligible(item: InboxItem): boolean {
  if (item.deleted_at || item.ai_status === "queued") return false;
  if (item.import_source === "uvoz_znr_agencija" && !["novo", "predlozeno"].includes(item.status)) return false;
  return item.owner_kind === "radnik" ? ["novo", "predlozeno", "potvrdeno"].includes(item.status)
    : item.import_source === "uvoz_znr_agencija" && item.owner_kind === "firma" && !item.radnik_id
      && item.firma_kategorija === "ostalo: Nerazvrstani paket agencije" && inboxPending(item);
}
export async function retryInboxAiBatch(ids: string[]): Promise<{ queued: number }> {
  return (await api.post<{ queued: number }>("/inbox/ai/backlog", { force: true, ids })).data;
}
export async function confirmInboxBatch(body: { ids: string[]; require_no_mismatch: boolean; confidence_threshold?: number }): Promise<InboxBatchResponse> {
  return (await api.post<InboxBatchResponse>("/inbox/confirm-batch", body)).data;
}
export async function rejectInboxBatch(ids: string[], reject_reason: string): Promise<InboxBatchResponse> {
  return (await api.post<InboxBatchResponse>("/inbox/reject-batch", { ids, reject_reason })).data;
}

export type InboxUploadSource = "drop" | "picker" | "camera";
export type InboxSource = InboxUploadSource | "scanner" | "webdav";
export type InboxOwner =
  | { owner_kind: "obitelj"; family_member_id: string }
  | { owner_kind: "radnik"; radnik_id: string }
  | { owner_kind: "gradiliste"; gradiliste_id: string }
  | { owner_kind: "firma"; firma_kategorija: string };

export const COMPANY_CATEGORIES = {
  "procjena-rizika": "Procjena rizika",
  ispitivanja: "Ispitivanja (oprema, instalacije, radni okoliš)",
  "zastita-od-pozara": "Zaštita od požara",
  ugovori: "Ugovori",
  osiguranje: "Osiguranje",
  "dozvole-potvrde": "Dozvole i potvrde",
  racunovodstvo: "Računovodstvo",
  ostalo: "Ostalo",
} as const;

export const MAX_INBOX_FILE_SIZE = 15 * 1024 * 1024;
export const INBOX_ACCEPT = "application/pdf,image/jpeg,image/png";

export function validateInboxFile(file: File): string | null {
  if (file.size > MAX_INBOX_FILE_SIZE) return "Datoteka je veća od 15 MB.";
  if (!INBOX_ACCEPT.split(",").includes(file.type)) {
    return "Dopušteni su samo PDF, JPEG i PNG.";
  }
  if (file.size === 0) return "Datoteka je prazna.";
  return null;
}

export interface InboxSite { id: string; naziv: string; aktivan?: boolean; povijesni_nositelj?: boolean }

export async function getInboxSites(signal?: AbortSignal): Promise<InboxSite[]> {
  return (await api.get<InboxSite[]>("/inbox/gradilista", { signal })).data;
}

export type InboxUploadResult = {
  index: number;
  izvorni_naziv: string;
} & (
  | { inbox_id: string; status: "novo" | "duplikat"; error?: never }
  | { error: { code: string; message: string }; status?: never }
);

export async function uploadInboxFiles(
  files: File[], owner: InboxOwner, source: InboxUploadSource, batchId: string,
): Promise<{ batch_id: string; results: InboxUploadResult[] }> {
  // Sending a stack takes minutes with nothing to click. The idle clock must
  // not read that as an empty room.
  oznaciAktivnost();
  const data = new FormData();
  files.forEach((file) => data.append("files", file));
  Object.entries(owner).forEach(([key, value]) => data.append(key, value));
  data.append("source", source);
  data.append("batch_id", batchId);
  return (await api.post<{ batch_id: string; results: InboxUploadResult[] }>(
    "/inbox/upload", data,
  )).data;
}

/**
 * Withdraw an entry the operator has just sent, or the office is clearing.
 *
 * Returns how many duplicate markers of the same file went with it, which is
 * more than none whenever the sheet was scanned twice.
 */
export async function deleteInboxItem(inboxId: string, reason?: string): Promise<number> {
  return (await api.delete<{ deleted_duplicates: number }>(`/inbox/${inboxId}`, { data: { reason: reason?.trim() || null } })).data.deleted_duplicates;
}

/** Preserve source metadata while keeping each multipart request small. */
export function groupInboxFiles<T extends { source: InboxUploadSource }>(items: T[]): T[][] {
  const groups: T[][] = [];
  // Every source the queue can hold. A source missing from this list would be
  // filtered out of every group and silently never sent.
  for (const source of ["drop", "picker", "camera"] as const) {
    const matching = items.filter((item) => item.source === source);
    for (let index = 0; index < matching.length; index += 20) {
      groups.push(matching.slice(index, index + 20));
    }
  }
  return groups;
}

interface BackfillItem { id: string; employee_document_id: string; radnik_id: string; owner_label: string; document_type_code: string; document_type_name: string; naziv: string; opis: string | null; issued_on: string | null; expires_on: string | null; has_ai_proposal: boolean; mime_type: string | null; original_filename: string | null }
function backfillItem(item: BackfillItem): InboxItem {
  return { id: item.employee_document_id, employee_document_id: item.employee_document_id, structured_backfill: true,
    owner_kind: "radnik", radnik_id: item.radnik_id, owner_label: item.owner_label, gradiliste_id: null, firma_kategorija: null,
    status: "potvrdeno", ai_status: item.has_ai_proposal ? "done" : "none", ai_title: item.naziv, ai_summary: item.opis,
    ai_confidence: null, ai_owner_mismatch: false, ai_error: null, ai_proposed_document_type_id: null,
    ai_proposed_issued_on: item.issued_on, ai_proposed_expires_on: item.expires_on,
    proposed_type_code: item.document_type_code, proposed_type_name: item.document_type_name,
    mime_type: item.mime_type, original_filename: item.original_filename,
    uploaded_at: "", uploaded_by_id: "", source: "picker", batch_id: "" };
}
export async function getStructuredBackfill(signal?: AbortSignal): Promise<InboxItem[]> {
  const items: InboxItem[] = [];
  for (let offset = 0; ; offset += 100) {
    const page = (await api.post<{ items: BackfillItem[]; total: number }>("/inbox/structured-backfill", { limit: 100, offset }, { signal })).data;
    items.push(...page.items.map(backfillItem));
    if (offset + page.items.length >= page.total || !page.items.length) return items;
  }
}

export async function restoreInboxItem(id: string): Promise<InboxItem> { return (await api.post<InboxItem>(`/inbox/${id}/restore`)).data; }
